import * as THREE from 'three';
import { settings } from '@/config';
import { smoothingFactor } from '@/utils/math';
import type { CameraController } from './CameraController';
import type { TickState, Updatable } from '@/types';

/**
 * Subtle mouse-driven camera LOOK: rotates the camera a little toward wherever the
 * pointer is (up/down/left/right) — it never moves the camera's position, just a
 * gentle parallax tilt. Writes into `CameraController.parallax`, a dormant quaternion
 * hook that was already post-multiplied onto the target rotation every frame
 * specifically for this, so it composes with the baked/scroll camera instead of
 * fighting it (and does nothing while `OrbitControlsManager` has taken the camera —
 * `CameraController.update()` early-returns while disabled, so writing `parallax`
 * during orbit mode is harmless dead work, not a bug needing extra guards).
 */
export class MouseLook implements Updatable {
  enabled = settings.camera.mouseLook.enabled;

  private readonly dom: HTMLElement;
  private readonly targetOffset = new THREE.Vector2(); // normalized pointer, -1..1
  private readonly current = new THREE.Vector2();
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');

  constructor(
    private readonly controller: CameraController,
    dom: HTMLElement,
  ) {
    this.dom = dom;
    this.dom.addEventListener('pointermove', this.onPointerMove, { passive: true });
  }

  private onPointerMove = (event: PointerEvent): void => {
    const rect = this.dom.getBoundingClientRect();
    this.targetOffset.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.targetOffset.y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
  };

  update(tick: TickState): void {
    const cfg = settings.camera.mouseLook;
    if (!this.enabled) {
      this.controller.parallax.identity();
      return;
    }

    const a = smoothingFactor(cfg.smoothing, tick.delta);
    this.current.lerp(this.targetOffset, a);

    // Mouse right → look right; mouse down → look down (natural, not inverted).
    const yaw = THREE.MathUtils.degToRad(-cfg.maxYawDeg) * this.current.x;
    const pitch = THREE.MathUtils.degToRad(-cfg.maxPitchDeg) * this.current.y;
    this.euler.set(pitch, yaw, 0, 'YXZ');
    this.controller.parallax.setFromEuler(this.euler);
  }

  dispose(): void {
    this.dom.removeEventListener('pointermove', this.onPointerMove);
    this.controller.parallax.identity();
  }
}
